<template>
  <div class="c-chargify-form">
    <div
      v-if="loading && canSubmitToChargify"
      class="flex justify-center items-center h-[240px]"
    >
      <img class="w-64" src="/images/gifs/loading.gif" />
    </div>
    <div v-show="!loading" class="h-full flex flex-col justify-between">
      <div v-show="!onlyFooter">
        <div :class="`px-${xMargin}`">
          <slot name="payer-info" />
        </div>
        <ChargifyIframes
          :chargify-client
          :editing
          @form-ref-saved="(form) => (chargifyForm = form)"
          @loaded="() => (loading = false)"
          @payment-portal-error="onPaymentPortalError"
        />
      </div>
      <div
        :class="{
          'z-40 fixed w-full flex flex-col-reverse items-center gap-16 pr-36 pb-24':
            isSmall,
          [`px-${xMargin}`]: true,
          'flex items-center justify-between border-t-none gap-24 pb-0':
            editing,
        }"
        class="form-buttons"
      >
        <BittsButton
          type="neutral"
          size="large"
          variant="outline"
          data-testid="chargify-cancel-button"
          text="Cancel"
          :class="{
            'w-full': isSmall,
            'flex-1': editing,
          }"
          @click="$emit('cancel')"
        />
        <BittsButton
          :text="submitButtonText"
          size="large"
          data-testid="chargify-form-submit-button"
          :disabled="disabled || fetchingToken"
          :loading="fetchingToken"
          :class="{
            'w-full': isSmall,
            'flex-1': editing,
          }"
          @click.prevent="handleSubmitForm"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { BittsButton } from '@crossbeam/bitts';
import { useScreenSize } from '@crossbeam/pointbreak';

import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import ChargifyIframes from '@/components/billing/ChargifyIframes.vue';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: 'Pay now',
  },
  fieldColumns: {
    type: Number,
    default: 2,
  },
  canSubmitToChargify: {
    type: Boolean,
    default: false,
  },
  fetchingToken: {
    type: Boolean,
    default: false,
  },
  editing: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'fetching-token',
  'token',
  'token-failure',
  'cancel',
  'next-screen',
]);

const { isSmall } = useScreenSize();

const router = useRouter();
const chargifyClient = new Chargify();
const chargifyForm = ref(null);

const onlyFooter = computed(() => !props.canSubmitToChargify);
const xMargin = computed(() => {
  if (isSmall.value && !props.editing) return 16;
  if (!isSmall.value && !props.editing) return 64;
  return 0;
});

async function handleSubmitForm() {
  if (!props.canSubmitToChargify) {
    emit('next-screen');
    return;
  }

  try {
    emit('fetching-token');
    const token = await handleGetToken();
    emit('token', token);
  } catch (err) {
    loading.value = false;
    emit('token-failure', err);
  }
}
function handleGetToken() {
  const form = chargifyForm.value;
  return new Promise((resolve, reject) => {
    chargifyClient.token(
      form,
      (token) => {
        resolve(token);
      },
      (error) => {
        reject(error);
      },
    );
  });
}

const loading = ref(true);

async function onPaymentPortalError() {
  await router.push({ name: 'billing' });
}
</script>
<style lang="pcss">
.c-chargify-form {
  #chargify-cvv > iframe:nth-child(1) {
    @apply outline-none !important;
  }

  #chargify-form > iframe {
    @apply w-full outline-none !important;
  }

  #chargify-form {
    display: flex;
  }

  #chargify-form iframe {
    width: 100% !important;
  }

  .form-buttons {
    @apply flex py-24 border-t border-neutral-border px-80 sticky bottom-0 bg-white;
  }

  .chargify-fields {
    @apply grid;
    grid-template-columns: repeat(v-bind(fieldColumns), 1fr);
  }
}
</style>
